/** @flow */

import http from "@http";
import config from "@config";
import actionCreators from "@modules/reporting/actionCreator";
import { all, call, put, takeEvery } from "redux-saga/effects";
import type { Action } from "redux-action";
import moment from "moment";
import {
  GetActivityFeedDashboard,
  GetFirstCareIndexPageStatistic,
  GetHierarchyBreakdown,
  GetRecentActivityFeedDashboard,
} from "@modules/reporting/constants/actions";
import camelcase from "camelize";

type ActivityFeedDashboardRequest = $Exact<{
  clientId: number,
}>;

export function* onGetActivityFeedDashboard({
  payload,
}: Action<ActivityFeedDashboardRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const activityFeedRes = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/tools/client/${payload.clientId}/tools/activity-feed`,
    );

    yield put(actionCreators.GetActivityFeedDashboardSuccess(activityFeedRes.data));
  } catch (e) {
    yield put(actionCreators.GetActivityFeedDashboardError(e.response));
  }
}

type DashboardFirstCareIndexRequest = $Exact<{
  clientId: number,
  fromDate?: string,
  toDate?: string,
}>;
export function* onGetFirstCareIndexPageStatistic({
  payload,
}: Action<DashboardFirstCareIndexRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const todaysDate = moment().format("YYYY-MM-DD");

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/tools/client/${payload.clientId}/tools/firstcareindex`,
      {
        params: {
          from_date: payload.fromDate || todaysDate,
          to_date: payload.toDate || todaysDate,
        },
      },
    );

    yield put(actionCreators.GetFirstCareIndexPageStatisticSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetFirstCareIndexPageStatisticError(e.response));
  }
}

export function* onGetHierarchyBreakdown({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/tools/client/${payload.clientId}/tools/dashboard/hierarchy-breakdown`,
    );

    yield put(actionCreators.GetHierarchyBreakdownSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetHierarchyBreakdownError(e.response));
  }
}

export function* onGetRecentActivityFeedDashboard({
  payload,
}: Action<ActivityFeedDashboardRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const recentActivitiesRes = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/tools/client/${payload.clientId}/tools/recent-activity`,
    );

    yield put(actionCreators.GetRecentActivityFeedDashboardSuccess(recentActivitiesRes.data));
  } catch (e) {
    yield put(actionCreators.GetRecentActivityFeedDashboardError(e.response));
  }
}

export default function* dashboardSaga(): Generator<*, void, void> {
  yield all([takeEvery(GetActivityFeedDashboard, onGetActivityFeedDashboard)]);
  yield all([takeEvery(GetRecentActivityFeedDashboard, onGetRecentActivityFeedDashboard)]);
  yield all([takeEvery(GetFirstCareIndexPageStatistic, onGetFirstCareIndexPageStatistic)]);
  yield all([takeEvery(GetHierarchyBreakdown, onGetHierarchyBreakdown)]);
}
